import Card from '@/designs/new/components/atom/card/card'
import BreadCrumbs from '../../components/atom/breadCrumbs/breadCrumbs'
import GradiantSkelton from '@/components/atom/gradiantSkelton/gradiantSkelton'
import WidgetX from './widgetX'
import { useState } from 'react'
import SummaryTab from './summary/summary'
import SitesTab from './sitesTab'
import OrdersTab from './ordersTab'
import { useAppSelector, useAppDispatch } from '@/hooks/storeHooks/storehooks'
import { setOverView } from '@/redux-store/slices/menuItemSlice/menuItemSlice'
import { useNavigate } from 'react-router-dom'
import { useEffect, useMemo, useCallback } from 'react'
import {
  fetchOverViewSiteMetrics,
  fetchOverViewOrderMetrics,
} from '@/services/apis/api'
import { formatDefPayload, getFromStorage, saveToStorage } from '@/lib/utils'
import {
  OverviewSiteMetricsProps,
  OverviewOrderMetricsProps,
} from '@/services/types/api.types'
import {
  setOverviewSiteMetrics,
  setOverviewOrderMetrics,
  setIsSitesMetricsLoading,
  setIsOrderMetricsLoading,
} from '@/redux-store/slices/OverviewSlice/SitesOverViewSlice'
import { toast } from 'sonner'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable'
import { SortableCard } from '@/designs/new/components/atom/sortableSection/SortableCard'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { animated, useSpring } from '@react-spring/web'
import MobileServiceDeliveryOverview from '../../components/mobile/MobileServiceDeliveryOverview'

const tabList = ['Summary', 'Sites', 'Orders']
const SERVICE_OVERVIEW_CARD_IDS = ['sites', 'orders']

export const ServiceOverView = () => {
  const [activeTab, setActiveTab] = useState('Summary')
  const [overviewCards, setOverviewCards] = useState(() => {
    const saved = getFromStorage('service_overviewCards')
    return Array.isArray(saved) ? saved : SERVICE_OVERVIEW_CARD_IDS
  })
  useEffect(() => {
    saveToStorage('service_overviewCards', overviewCards)
  }, [overviewCards])
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  )

  const defPayload = useAppSelector(state => state.menu.payloadDef)
  const essentialIds = useMemo(() => formatDefPayload(defPayload), [defPayload])

  const siteMetricsData = useAppSelector(
    state => state.sitesOverview.overViewSiteMetrics,
  ) as { total: number; completed: number; inProgress: number; pastDue: number }

  const orderMetricsData = useAppSelector(
    state => state.sitesOverview.overViewOrderMetrics,
  ) as { total: number; completed: number; inProgress: number; pastDue: number }

  const isSitesLoading = useAppSelector(
    state => state.sitesOverview.isSiteMetricsLoading,
  )
  const isOrdersLoading = useAppSelector(
    state => state.sitesOverview.isOrderMetricsLoading,
  )

  const handleSitesOverview = (item: { name: string }) => () => {
    navigate('/service-delivery/sites', { state: { item } })
  }

  const handleOrdersOverview = (key: { name: string }) => () => {
    navigate('/service-delivery/orders', { state: { key } })
  }

  const handleOverviewDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setOverviewCards((items: string[]) => {
        const oldIndex = items.indexOf(active.id as string)
        const newIndex = items.indexOf(over.id as string)

        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const getOverviewSiteMetrics = useCallback(async () => {
    dispatch(setIsSitesMetricsLoading(true))
    const res = (await fetchOverViewSiteMetrics({
      ...essentialIds,
    })) as OverviewSiteMetricsProps

    if (res.status === 200) {
      dispatch(setOverviewSiteMetrics(res.data.data.metrics))
    } else {
      toast.error('Something Went Wrong')
    }
    dispatch(setIsSitesMetricsLoading(false))
  }, [dispatch, essentialIds])

  const getOverviewOrderMetrics = useCallback(async () => {
    dispatch(setIsOrderMetricsLoading(true))
    const res = (await fetchOverViewOrderMetrics({
      ...essentialIds,
    })) as OverviewOrderMetricsProps

    if (res.status === 200) {
      dispatch(setOverviewOrderMetrics(res.data.data.metrics))
    } else {
      toast.error('Something Went Wrong')
    }
    dispatch(setIsOrderMetricsLoading(false))
  }, [dispatch, essentialIds])

  useEffect(() => {
    if (defPayload.customerId && defPayload.siteId.length > 0) {
      getOverviewSiteMetrics()
      getOverviewOrderMetrics()
    }
  }, [
    defPayload.customerId,
    defPayload.siteId.length,
    getOverviewSiteMetrics,
    getOverviewOrderMetrics,
  ])

  const selectedOptions = useAppSelector(
    state => state.dropdownOptionsClicked?.selectedOptions,
  )

  const isWidgetVisible = (widgetKey: string) => {
    const widget = selectedOptions?.find(
      (opt: { key: string; value: boolean }) => opt.key === widgetKey,
    )
    return widget ? widget.value : true
  }

  const renderOverviewCard = (cardId: string) => {
    switch (cardId) {
      case 'sites':
        if (!isWidgetVisible('sites')) return null
        return (
          <SortableCard key={cardId} id={cardId} className='h-full'>
            {isSitesLoading ? (
              <div className='w-full h-[180px] p-3 bg-primary border border-border border-l-[4px] border-l-accent rounded-xl shadow-sm'>
                <div className='mb-6 w-20'>
                  <GradiantSkelton width='w-full' height='h-6' />
                </div>

                <div className='flex gap-4 justify-between'>
                  <GradiantSkelton width='w-full' height='h-16' />
                  <GradiantSkelton width='w-full' height='h-16' />
                  <GradiantSkelton width='w-full' height='h-16' />
                </div>
              </div>
            ) : (
              <WidgetX
                title='Sites'
                filterOptions={[]}
                metrics={[
                  {
                    label: 'Total',
                    value: siteMetricsData?.total || 0,
                    color: 'var(--info-forground)',
                    onClick: handleSitesOverview({
                      name: 'Total',
                    }),
                  },
                  {
                    label: 'Completed',
                    value: siteMetricsData?.completed || 0,
                    color: 'var(--success-forground)',
                    onClick: handleSitesOverview({
                      name: 'Completed',
                    }),
                  },
                  {
                    label: 'Ongoing',
                    value: siteMetricsData?.inProgress || 0,
                    color: 'var(--warning)',
                    onClick: handleSitesOverview({
                      name: 'Ongoing',
                    }),
                  },
                ]}
                dragDrop={true}
              />
            )}
          </SortableCard>
        )
      case 'orders':
        if (!isWidgetVisible('orders')) return null
        return (
          <SortableCard key={cardId} id={cardId} className='h-full'>
            {isOrdersLoading ? (
              <div className='w-full h-[180px] p-3 bg-primary border border-border border-l-[4px] border-l-accent rounded-xl shadow-sm'>
                <div className='mb-6 w-20'>
                  <GradiantSkelton width='w-full' height='h-6' />
                </div>

                <div className='flex gap-4 justify-between'>
                  <GradiantSkelton width='w-full' height='h-16' />
                  <GradiantSkelton width='w-full' height='h-16' />
                  <GradiantSkelton width='w-full' height='h-16' />
                </div>
              </div>
            ) : (
              <WidgetX
                title='Orders'
                filterOptions={[]}
                metrics={[
                  {
                    label: 'Total',
                    value: orderMetricsData?.total || 0,
                    color: 'var(--info-forground)',
                    onClick: handleOrdersOverview({
                      name: 'Total',
                    }),
                  },
                  {
                    label: 'Completed',
                    value: orderMetricsData?.completed || 0,
                    color: 'var(--success-forground)',
                    onClick: handleOrdersOverview({
                      name: 'Completed',
                    }),
                  },
                  {
                    label: 'Ongoing',
                    value: orderMetricsData?.inProgress || 0,
                    color: 'var(--warning)',
                    onClick: handleOrdersOverview({
                      name: 'Ongoing',
                    }),
                  },
                ]}
                dragDrop={true}
              />
            )}
          </SortableCard>
        )
      default:
        return null
    }
  }

  const renderSection = () => {
    return (
      <div className='flex flex-col flex-1 min-h-0'>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleOverviewDragEnd}
        >
          <SortableContext items={overviewCards} strategy={rectSortingStrategy}>
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3'>
              {overviewCards.map(renderOverviewCard)}
            </div>
          </SortableContext>
        </DndContext>
        <div className='h-[100%] flex flex-col pt-padding-5xl pb-4 text-[18px] font-400 text-primary-foreground md:max-w-[calc(100%-97px)] lg:max-w-none'>
          {/* Tabs */}
          <div className='w-72 flex border-b mb-4 space-x-6 justify-between'>
            {tabList.map(tab => (
              <div
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`cursor-pointer text-center pb-2 w-full ${
                  activeTab === tab
                    ? 'text-primary-active-foreground border-b-2 border-primary-active-foreground font-semibold'
                    : 'text-primary-foreground'
                }`}
              >
                {tab}
              </div>
            ))}
          </div>

          {/* Tab Content */}
          <div className='flex-1 min-h-0 w-full'>
            {activeTab === 'Summary' && (
              <SummaryTab isWidgetVisible={isWidgetVisible} />
            )}
            {activeTab === 'Sites' && (
              <SitesTab isWidgetVisible={isWidgetVisible} />
            )}
            {activeTab === 'Orders' && (
              <OrdersTab isWidgetVisible={isWidgetVisible} />
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='flex flex-col gap-4 h-full overflow-y-auto spec-scroll'>
      {renderSection()}
    </div>
  )
}
const ServiceDeliveryOverview = () => {
  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(setOverView('Service Delivery'))
    return () => {
      dispatch(setOverView('Home'))
    }
  }, [dispatch])

  const slideUp = useSpring({
    from: { transform: 'translateY(100px)', opacity: 0, height: '100%' },
    to: { transform: 'translateY(0px)', opacity: 1, height: '100%' },
    config: { tension: 180, friction: 20 },
  })
  const isMobile = useMediaQuery('(max-width: 768px)')
  const navigate = useNavigate()

  if (isMobile) {
    return (
      <animated.div style={slideUp}>
        <div className='md:hidden h-[calc(100vh-20px)] bg-primary w-full max-w-full overflow-x-hidden flex flex-col'>
          <div className='flex flex-col p-3 pb-0'>
            <BreadCrumbs />
            <h1 className='font-semibold text-lg text-primary-foreground'>
              Overview
            </h1>
          </div>
          <div className='flex-1 overflow-y-auto spec-scroll p-3 pt-3 pb-12'>
            <MobileServiceDeliveryOverview
              onSitesClick={() => navigate('/service-delivery/sites')}
              onOrdersClick={() => navigate('/service-delivery/orders')}
            />
          </div>
        </div>
      </animated.div>
    )
  }

  return (
    // Make sure the Card has a real bounded height, e.g.:
    <Card className='hidden w-full h-full p-padding-xl px-4 pt-4 flex-col gap-3 md:flex'>
      <BreadCrumbs />
      <div className=' flex justify-between items-center'>
        <h1 className='font-semibold py-1 text-page-title text-primary-foreground h-11 flex items-center'>
          Overview
        </h1>
      </div>
      <div className='flex-1 min-h-0'>
        <ServiceOverView />
      </div>
    </Card>
  )
}

export default ServiceDeliveryOverview
